import { windowReferenceService } from './window-reference.service';
import { Injectable } from '@angular/core';
import { SvgEditExtension, SvgEditExtensionObject } from '@models/svg-edit-extension';
import { CanvasService } from './canvas.service';
import { CanvasInputMode, CanvasInputModeService } from './canvas-input-mode.service';
import { SelectedElementsService } from './selected-elements.service';
import { ZoomService } from './zoom.service';
import { Subscription } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class PathService implements SvgEditExtension {
    name: string = 'pathExtension';

    constructor(private plugin: PathExtensionPlugin) {
        this.init = this.init.bind(this);
    }

    init(): SvgEditExtensionObject {
        this.bindMouseEvents();
        this.startZoomSubscription();
        return this.plugin;
    }

    addEventListeners() {}

    private bindMouseEvents() {
        return this.plugin.bindMouseEvents();
    }
    private startZoomSubscription() {
        return this.plugin.startZoomSubscription();
    }

    finishPath() {
        return this.plugin.finishPath();
    }
}

@Injectable({ providedIn: 'root' })
class PathExtensionPlugin implements SvgEditExtensionObject {
    constructor(
        private canvasService: CanvasService,
        private inputMode: CanvasInputModeService,
        private zoomService: ZoomService,
        private windowReference: windowReferenceService,
        private selectionService: SelectedElementsService,
        private canvasModeService: CanvasInputModeService
    ) {}

    zoomSubscription: Subscription;

    pathToUpdate: string = null;

    diffZoom: number;
    private isDrawingPath = false;

    callback(deps: any) {}

    bindMouseEvents() {
        document.addEventListener('mousedown', this.handleMouseDown.bind(this));
        document.addEventListener('mousemove', this.handleMouseMove.bind(this));
        document.addEventListener('mouseup', this.handleMouseUp.bind(this));
        document.addEventListener('keydown', this.handleKeyDown.bind(this));
        this.bindCanvasEvents();
    }

    private bindCanvasEvents() {
        const canvas = this.canvasService.getCanvas();
        canvas.bind('changed', (win, elements) => {
            const element = elements?.[0];

            if (!this.isDrawingPath || !element) {
                return;
            }
            if (element.tagName?.toLowerCase() !== 'path') {
                return;
            }
            this.selectionService.setSelectedElements([element]);
            this.isDrawingPath = false;
        });
    }

    addEventListeners() {}

    startZoomSubscription() {
        this.zoomSubscription = this.zoomService.onZoom().subscribe(() => {
            if (this.inputMode.getMode() === CanvasInputMode.PATH) {
                let canvas = this.canvasService.getCanvas();
                let drawnPathElem = canvas.getDrawnPath();
                let drawPath_ = canvas.getPath_(drawnPathElem);
                drawPath_.update();

                let stretchy = document.getElementById('path_stretch_line') as any;

                let zoom = this.diffZoom ? this.zoomService.getZoom() / this.diffZoom : this.zoomService.getZoom();

                let path = stretchy.getAttribute('d');
                if (!this.pathToUpdate) {
                    this.pathToUpdate = path;
                }
                let points = this.pathToUpdate.match(/[MLC][^MLC]*/g);

                if (!points || points.length < 2) {
                    console.error('Invalid path data');
                    return;
                }
                let startPoint = points[0].slice(1).trim().split(' ').map(Number);
                let nextPoint = points[1].slice(1).trim().split(' ').map(Number);
                let zoomedStartPoint = startPoint.map(coord => coord * zoom);
                let zoomedNextPoint = nextPoint.map(coord => coord * zoom);
                let newD = `M${zoomedStartPoint.join(' ')} ${points[1][0]}${zoomedNextPoint.join(' ')}`;
                stretchy.setAttribute('d', newD);
            }
        });
    }

    private handleKeyDown(event: KeyboardEvent) {
        if (this.inputMode.getMode() === CanvasInputMode.PATH) {
            if (event.key === 'Escape') {
                this.finishPath(event);
            }
        }
    }

    private handleMouseDown() {
        if (this.inputMode.getMode() === CanvasInputMode.PATH) {
            this.isDrawingPath = true;
            const selector = document.getElementById('selectorRubberBand');
            if (selector && selector.getAttribute('display') != 'none') {
                selector.setAttribute('display', 'none');
            }
            if (this.pathToUpdate) {
                this.pathToUpdate = null;
                const zoom = this.zoomService.getZoom();
                if (zoom != 1) {
                    this.diffZoom = zoom;
                } else {
                    this.diffZoom = null;
                }
            }
            const stretchy = document.getElementById('path_stretch_line') as any;
            if (stretchy) {
                const strokeColor = this.canvasService.getStrokeColorForBackground();
                stretchy.setAttribute('stroke', strokeColor);
            }
        } else if (this.pathToUpdate) {
            this.pathToUpdate = null;
            this.diffZoom = null;
        }
    }

    private handleMouseMove(event) {
        if (this.inputMode.getMode() === CanvasInputMode.PATH) {
            if (this.pathToUpdate) {
                let stretchy = document.getElementById('path_stretch_line') as any;
                let path = stretchy.getAttribute('d');
                let points = path.match(/[MLC][^MLC]*/g);

                if (!points || points.length < 2) {
                    console.error('Invalid path data');
                    return;
                }
                let startPoint = points[0].slice(1).trim().split(' ').map(Number);
                const rootSctm = (document.getElementById('selectorParentGroup') as any).getScreenCTM().inverse();
                const pt = this.canvasService.transformPoint(event.clientX, event.clientY, rootSctm);
                const mouseX = Number(pt.x);
                const mouseY = Number(pt.y);
                let newD = `M${startPoint.join(' ')} ${points[1][0]} ${mouseX} ${mouseY}`;
                stretchy.setAttribute('d', newD);
            }
        }
    }

    private handleMouseUp(event: MouseEvent) {
        if (this.inputMode.getMode() === CanvasInputMode.FREE_DRAW) {
            const target = event.target as any;
            this.canvasModeService.setMode(CanvasInputMode.SELECT);
            if (target) this.selectionService.setSelectedElements([target]);
        }
    }

    finishPath(event?: any) {
        this.pathToUpdate = null;
        const canvas = this.canvasService.getCanvas();
        const drawnPath = canvas.getDrawnPath();
        if (!drawnPath) {
            return;
        }
        const seglist = drawnPath.pathSegList;
        if (seglist.length <= 1) {
            this.inputMode.setMode(CanvasInputMode.SELECT);
            return;
        }
        let i = seglist.numberOfItems;
        const item = seglist.getItem(i - 1);
        const px = item.x;
        const py = item.y;
        const zoom = this.zoomService.getZoom();
        canvas.pathActions.mouseDown(event, drawnPath, px * zoom, py * zoom);
        canvas.pathActions.mouseUp(event, drawnPath, px * zoom, py * zoom);
        const curShape = canvas.getStyle();
        const strokeColor = this.canvasService.getStrokeColorForBackground();

        drawnPath.setAttribute('opacity', curShape.opacity);
        drawnPath.setAttribute('stroke', strokeColor);
        drawnPath.setAttribute('style', 'pointer-events:inherit');
        canvas.cleanupElement(drawnPath);
        canvas.pathActions.toEditMode(drawnPath);

        this.selectionService.setSelectedElements([drawnPath]);
        this.inputMode.setMode(CanvasInputMode.SELECT);
    }
}
